import { StaffApiSchema } from "@apiUtils/index";

type SettingsSearchResult = StaffApiSchema.components["schemas"]["SettingsSearchResult"];

/**
 * Query params appended to a subgroup panel URL to tell the page what to scroll to.
 * A result with neither param lands at the top of the page (group results).
 */
export const SUBGROUP_SEARCH_PARAM = "subgroup";
export const SETTING_SEARCH_PARAM = "setting";

const SUBGROUP_PANEL_BASE_PATH = "/configuration/institution_settings/categories";

export const subGroupAnchorId = (subgroupName: string) => `settings-subgroup-${subgroupName}`;

export const settingAnchorId = (settingName: string) => `settings-setting-${settingName}`;

export const subGroupPanelPath = (category: string, group: string) =>
  `${SUBGROUP_PANEL_BASE_PATH}/${encodeURIComponent(category)}/groups/${encodeURIComponent(group)}/subgroups`;

/**
 * Every search result deep-links to the subgroup panel for its category/group.
 * The result type only controls where within that page we scroll to:
 * group -> top of the page, subgroup -> its SubGroupCard, setting -> the setting itself.
 */
export const searchResultPath = (result: SettingsSearchResult): string => {
  const path = subGroupPanelPath(result.category, result.group);
  const params = new URLSearchParams();

  if (result.result_type !== "group" && result.subgroup) {
    params.set(SUBGROUP_SEARCH_PARAM, result.subgroup);
  }
  if (result.result_type === "setting" && result.name) {
    params.set(SETTING_SEARCH_PARAM, result.name);
  }

  const queryString = params.toString();
  return queryString ? `${path}?${queryString}` : path;
};

/** Stable React key for a result. No single field is unique across all three result types. */
export const searchResultKey = (result: SettingsSearchResult): string =>
  [
    result.result_type,
    result.category,
    result.group,
    result.subgroup ?? "",
    result.name ?? "",
  ].join("|");
